import { FC, useCallback, useEffect } from 'react';
import { CoreCheckbox } from '../../_core';
import { RightBlackModal } from '../../right-modal';
import { customResetForm } from '../../../utils';
import { EventBundleDto } from '@sweatpals/shared';
import { FormGroup, Typography, Box } from '@mui/material';
import { useForm, useFormContext, useWatch, FormProvider } from 'react-hook-form';

export interface EventBundleModalProps {
	open: boolean;
	communityEventBundles: EventBundleDto[];
	data?: string[];
	onClose(isDirty?: boolean): void;
	onSubmit?(data: string[]): void;
}
type EventBundleModalFormValues = {
  eventBundleMap: Record<string, boolean>;
};

const EventBundleCard = ({ bundle }: { bundle: EventBundleDto }) => {
  const form = useFormContext<EventBundleModalFormValues>();
  const { setValue } = form;
  const isChecked = useWatch({
    name: `eventBundleMap.${bundle.id}`,
    control: form.control,
  });

  return (
    <Box key={bundle.id} sx={{ display: 'flex', alignItems: 'center' }}>
      <CoreCheckbox
        checked={isChecked}
        onChange={(e) => {
          setValue(`eventBundleMap.${bundle.id}`, e.target.checked)
        }}
        name={`eventBundleMap.${bundle.id}`}
      />
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          fontFamily: 'Montserrat',
          color: '#fff',
        }}
      >
        {bundle.name} ({bundle.visitsAmount} events)
      </Typography>
    </Box>
  );
};

export const EventBundleModal: FC<EventBundleModalProps> = ({
  open,
  communityEventBundles,
  data = [],
  onClose,
  onSubmit,
}) => {
  const defaultMap = Object.fromEntries(
    communityEventBundles.map((bundle) => [bundle.id, data.includes(bundle.id)])
  );

  const form = useForm<EventBundleModalFormValues>({
    defaultValues: { eventBundleMap: defaultMap },
    mode: 'onBlur',
  });

  const handleSave = useCallback(
    (formData: EventBundleModalFormValues) => {
      const selectedIds = Object.entries(formData.eventBundleMap)
        .filter(([, checked]) => checked)
        .map(([id]) => id);
      onSubmit?.(selectedIds);
    },
    [onSubmit]
  );

  const handleClose = useCallback(() => {
    onClose?.(form.formState.isDirty);
  }, [onClose, form.formState.isDirty]);

  useEffect(() => {
    if (open) {
      const updatedMap = Object.fromEntries(
        communityEventBundles.map((bundle) => [bundle.id, data.includes(bundle.id)])
      );
      customResetForm(form, { eventBundleMap: updatedMap });
    }
  }, [open, data, communityEventBundles, form]);

  return (
    <RightBlackModal
      open={open}
      onClose={handleClose}
      headerTitle="Event Bundle Settings"
      headerDescription="Choose Event Bundles that can be applied for this event:"
      modalProps={{
        component: 'form',
        onSubmit: form.handleSubmit(handleSave),
      }}
      submitButtonProps={{ type: 'submit' }}
    >
      <FormProvider {...form}>
        <FormGroup sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {communityEventBundles.map((bundle) => (
            <EventBundleCard key={bundle.id} bundle={bundle} />
          ))}
        </FormGroup>
      </FormProvider>
    </RightBlackModal>
  );
};
